/**
 * Helpers compartilhados do calendário.
 *
 * Centraliza:
 *  - Conversão de horário UTC → BRT (America/Sao_Paulo) via Intl (sem depender
 *    de date-fns-tz, que não está instalado no projeto).
 *  - Cor determinística por profissional (hash → HSL). Fonte única de verdade,
 *    usada tanto pelo pai (CalendarAgenda) quanto pelo CalendarCalendar.
 *  - Geometria da grade (slots, minutos desde o início do expediente).
 */

export const TIME_ZONE = "America/Sao_Paulo";

// Fallback do expediente quando o tenant não tem businessHours configurado.
// A faixa real é derivada dinamicamente (ver computeGridRange).
export const DEFAULT_GRID_START_HOUR = 8;
export const DEFAULT_GRID_END_HOUR = 18; // exclusivo — última linha é 18:00

// Retrocompat: alguns consumidores ainda importam estes nomes. Mantemos como
// alias do default; a geometria real agora vem de uma faixa dinâmica.
export const GRID_START_HOUR = DEFAULT_GRID_START_HOUR;
export const GRID_END_HOUR = DEFAULT_GRID_END_HOUR;

// Pixels por hora na grade (configurável). 64px = 16 * 4 slots de 15min.
export const HOUR_HEIGHT_PX = 64;
export const GRID_MINUTES = (GRID_END_HOUR - GRID_START_HOUR) * 60;
export const GRID_HEIGHT_PX = (GRID_MINUTES / 60) * HOUR_HEIGHT_PX;
// Altura mínima de um card, para que agendamentos curtos continuem clicáveis.
export const MIN_EVENT_HEIGHT_PX = 24;

/**
 * Formata um instante (ISO/Date) em string "HH:mm" no fuso de Brasília.
 * @param {string|Date} value ISO UTC ou Date.
 * @returns {string} "HH:mm" em BRT, ou "" se inválido.
 */
export function formatTimeBRT(value) {
  if (!value) return "";
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  return new Intl.DateTimeFormat("pt-BR", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
    timeZone: TIME_ZONE,
  }).format(d);
}

/**
 * Retorna { hour, minute } de um instante já convertido para BRT.
 * @param {string|Date} value
 * @returns {{ hour: number, minute: number }}
 */
export function getBRTParts(value) {
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return { hour: 0, minute: 0 };
  const parts = new Intl.DateTimeFormat("en-US", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
    timeZone: TIME_ZONE,
  }).formatToParts(d);
  let hour = 0;
  let minute = 0;
  for (const p of parts) {
    if (p.type === "hour") hour = parseInt(p.value, 10) % 24;
    if (p.type === "minute") minute = parseInt(p.value, 10);
  }
  return { hour, minute };
}

/**
 * Minutos desde uma hora-base (gridStartHour) em BRT.
 * Pode ser negativo (evento antes da hora-base) — quem consome decide o clamp.
 * @param {string|Date} value
 * @param {number} [gridStartHour=GRID_START_HOUR] Hora-base da grade.
 * @returns {number}
 */
export function minutesFromGridStart(value, gridStartHour = GRID_START_HOUR) {
  const { hour, minute } = getBRTParts(value);
  return (hour - gridStartHour) * 60 + minute;
}

/**
 * Retorna a chave de dia (yyyy-MM-dd) de um instante em BRT.
 * @param {string|Date} value
 * @returns {string} "yyyy-MM-dd"
 */
export function dayKeyBRT(value) {
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  const parts = new Intl.DateTimeFormat("en-CA", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    timeZone: TIME_ZONE,
  }).formatToParts(d);
  let y = "";
  let m = "";
  let day = "";
  for (const p of parts) {
    if (p.type === "year") y = p.value;
    if (p.type === "month") m = p.value;
    if (p.type === "day") day = p.value;
  }
  return `${y}-${m}-${day}`;
}

/**
 * Cor de acento determinística para um profissional.
 * Hash simples do id → hue; saturação/luminosidade fixas. Sem profissional
 * recebe o azul padrão da marca.
 * @param {string|null|undefined} professionalId
 * @returns {string} cor HSL.
 */
export function getProfessionalColor(professionalId) {
  if (!professionalId) return "hsl(217, 70%, 60%)";
  return stringToColor(professionalId);
}

/**
 * Cor HSL determinística a partir de uma string arbitrária (nome, id, etc.).
 * Mesma lógica de hash usada por `getProfessionalColor` — fonte única de verdade
 * para gerar cores de avatar em qualquer parte do app.
 * @param {string|null|undefined} value
 * @returns {string} cor HSL `hsl(hue, 65%, 55%)`.
 */
export function stringToColor(value) {
  if (!value) return "hsl(217, 70%, 60%)";

  let hash = 0;
  for (let i = 0; i < value.length; i++) {
    hash = (hash << 5) - hash + value.charCodeAt(i);
    hash |= 0;
  }

  const hue = Math.abs(hash) % 360;
  return `hsl(${hue}, 65%, 55%)`;
}

/**
 * Duração em minutos de um agendamento. Prefere service.duration; cai para
 * (endTime - startTime). Garante mínimo de 15 min.
 * @param {{ startTime: string, endTime?: string, service?: { duration?: number } }} app
 * @returns {number}
 */
export function getDurationMinutes(app) {
  if (app?.service?.duration) return app.service.duration;
  if (app?.endTime && app?.startTime) {
    const diff = (new Date(app.endTime) - new Date(app.startTime)) / 60000;
    if (diff > 0) return Math.round(diff);
  }
  return 15;
}

/**
 * Instante final do agendamento como timestamp (ms). Usa endTime quando
 * disponível; senão deriva de startTime + duração.
 * @param {{ startTime: string, endTime?: string, service?: { duration?: number } }} app
 * @returns {number} epoch ms
 */
export function getEndMs(app) {
  if (app?.endTime) {
    const end = new Date(app.endTime).getTime();
    if (!Number.isNaN(end)) return end;
  }
  return new Date(app.startTime).getTime() + getDurationMinutes(app) * 60000;
}

/**
 * Formata um preço numérico em BRL. Retorna "" se não houver preço.
 * @param {number|null|undefined} price
 * @returns {string}
 */
export function formatPriceBRL(price) {
  if (price == null || Number.isNaN(Number(price))) return "";
  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
  }).format(Number(price));
}

// ── Expediente dinâmico ──────────────────────────────────────────────────────

/**
 * Converte "HH:mm" em hora fracionária (ex.: "08:30" → 8.5). Retorna null se
 * inválido.
 * @param {string|null|undefined} value
 * @returns {number|null}
 */
function parseHourString(value) {
  if (typeof value !== "string") return null;
  const m = /^(\d{1,2}):(\d{2})$/.exec(value.trim());
  if (!m) return null;
  const hour = parseInt(m[1], 10);
  const minute = parseInt(m[2], 10);
  if (Number.isNaN(hour) || Number.isNaN(minute)) return null;
  return hour + minute / 60;
}

/**
 * Deriva a faixa de horas [open, close] do expediente a partir do shape
 * `businessHours` do tenant. O shape canônico é um mapa de dia → tupla
 * `[abertura, fechamento]` (ex.: `{ seg: ["08:00","18:00"], sab: ["09:00","13:00"] }`);
 * dias fechados são simplesmente ausentes. Por robustez também aceitamos o
 * range legado `"seg-sex": [...]` (mesma estrutura de tupla).
 *
 * Considera o menor horário de abertura e o maior de fechamento entre os dias
 * presentes. Retorna `null` quando não há expediente utilizável (deixando o
 * fallback a cargo de computeGridRange).
 *
 * @param {object|null|undefined} businessHours
 * @returns {{ openHour: number, closeHour: number } | null}
 */
export function businessHoursRange(businessHours) {
  if (!businessHours || typeof businessHours !== "object") return null;
  if (Array.isArray(businessHours)) return null;

  let minOpen = Infinity;
  let maxClose = -Infinity;
  for (const key of Object.keys(businessHours)) {
    const range = businessHours[key];
    if (!Array.isArray(range) || range.length !== 2) continue;
    const open = parseHourString(range[0]);
    const close = parseHourString(range[1]);
    if (open != null) minOpen = Math.min(minOpen, open);
    if (close != null) maxClose = Math.max(maxClose, close);
  }
  if (!Number.isFinite(minOpen) || !Number.isFinite(maxClose)) return null;
  if (maxClose <= minOpen) return null;
  return { openHour: minOpen, closeHour: maxClose };
}

/**
 * Calcula a faixa horária (em horas inteiras) que a grade deve renderizar,
 * unindo o expediente do tenant com TODOS os agendamentos visíveis para
 * garantir que nenhum evento seja escondido/clampado.
 *
 *  - Base: `businessHours` do tenant (ou fallback 08:00–18:00).
 *  - Expansão: para baixo até o menor startTime BRT e para cima até o maior
 *    endTime BRT entre os `appointments`.
 *  - Arredonda para horas inteiras (floor no início, ceil no fim) para alinhar
 *    com as labels de hora da coluna lateral.
 *
 * @param {Array<{ startTime: string, endTime?: string, service?: object }>} appointments
 * @param {object|null|undefined} businessHours
 * @returns {{ startHour: number, endHour: number }} Horas inteiras [startHour, endHour].
 */
export function computeGridRange(appointments, businessHours) {
  const bh = businessHoursRange(businessHours);
  let startHour = bh ? bh.openHour : DEFAULT_GRID_START_HOUR;
  let endHour = bh ? bh.closeHour : DEFAULT_GRID_END_HOUR;

  for (const app of appointments ?? []) {
    if (!app?.startTime) continue;
    const start = getBRTParts(app.startTime);
    const startFrac = start.hour + start.minute / 60;
    if (startFrac < startHour) startHour = startFrac;

    const endMs = getEndMs(app);
    if (!Number.isNaN(endMs)) {
      const end = getBRTParts(new Date(endMs));
      const endFrac = end.hour + end.minute / 60;
      // Caso o evento atravesse meia-noite em BRT, ancoramos no fim do dia.
      const adjustedEnd = endFrac < startFrac ? 24 : endFrac;
      if (adjustedEnd > endHour) endHour = adjustedEnd;
    }
  }

  startHour = Math.floor(startHour);
  endHour = Math.ceil(endHour);
  if (endHour <= startHour) endHour = startHour + 1;
  startHour = Math.max(0, startHour);
  endHour = Math.min(24, endHour);
  return { startHour, endHour };
}

// ── Badges de status por segmento ────────────────────────────────────────────

/**
 * Mapa centralizado de cores de badge por status, coerente com a paleta Nexus.
 *
 *  - terminais positivos (confirmed) e "concluído" (finalizado/pronto) → verde/teal;
 *  - cancelled → vermelho;
 *  - pendentes/aguardando → âmbar;
 *  - em andamento (em_atendimento/em_execucao) → azul Nexus (#3498DB).
 *
 * Cada entrada traz `bg` (cor de fundo do badge, texto sempre branco).
 */
export const STATUS_BADGE_COLORS = {
  confirmed: { bg: "rgba(39, 174, 96, 0.92)" },
  finalizado: { bg: "rgba(22, 160, 133, 0.95)" },
  pronto: { bg: "rgba(22, 160, 133, 0.95)" },
  em_atendimento: { bg: "rgba(52, 152, 219, 0.95)" }, // Nexus Blue
  em_execucao: { bg: "rgba(52, 152, 219, 0.95)" },
  pending: { bg: "rgba(243, 156, 18, 0.95)" },
  aguardando_peca: { bg: "rgba(230, 126, 34, 0.95)" }, // âmbar
  cancelled: { bg: "rgba(231, 76, 60, 0.92)" },
};

// Cor neutra para status fora do mapa conhecido (mas presente no catálogo).
const NEUTRAL_BADGE_BG = "rgba(127, 140, 141, 0.92)";

/**
 * Mapa de status → "tone" semântico, consumido pelo pill do AppointmentCard
 * para escolher a família de tokens (--nx-*-bg / --nx-*). Mantém paridade com
 * STATUS_BADGE_COLORS, mas em vez de cor-hardcoded entrega um rótulo semântico:
 *
 *  - cancelled                                  → danger
 *  - finalizado / pronto                        → success
 *  - em_atendimento / em_execucao / aguardando_peca → warning
 *  - confirmed                                  → neutral
 *
 * Status fora do mapa (mas presente no catálogo do segmento) → neutral.
 */
const STATUS_TONE = {
  confirmed: "neutral",
  finalizado: "success",
  pronto: "success",
  em_atendimento: "warning",
  em_execucao: "warning",
  aguardando_peca: "warning",
  pending: "warning",
  cancelled: "danger",
};

/**
 * Resolve o badge a exibir para um agendamento, derivando o rótulo do catálogo
 * de status do segmento (`statusOptions`) e a cor do mapa centralizado.
 *
 * Retorna `null` quando o status não existe no catálogo do segmento — nesse
 * caso o card não exibe badge (evita vazar status de outro segmento).
 *
 * @param {string|null|undefined} status Status persistido do agendamento.
 * @param {Array<{ value: string, label: string }>} statusOptions Catálogo do segmento.
 * @returns {{ label: string, bg: string, tone: string } | null}
 */
export function resolveStatusBadge(status, statusOptions) {
  if (!status) return null;
  const option = (statusOptions ?? []).find((o) => o.value === status);
  if (!option) return null;
  const color = STATUS_BADGE_COLORS[status] ?? { bg: NEUTRAL_BADGE_BG };
  const tone = STATUS_TONE[status] ?? "neutral";
  return { label: option.label, bg: color.bg, tone };
}
